import { Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../../../../../../_augmentt/selectors/customers'
import { ModalPanelSwitch, ModalSidebar } from '../../../../../components/ModalSidebar'
import useEngageResetPassword from '../../../../../hooks/useEngageResetPassword'
import { generatePassword } from '../../../../../util/auth.util'
import { getEmployeeFullName } from '../../../../../util/table.util'
import { useEngageEmployees } from '../../../utils/engage'
import { buildInitialValues, getValidationSchema, toApiPayload } from '../common/PasswordSettingsSection/fieldset'
import { MODAL_STEPS, STEP_KEYS } from './constants'
import ResetPasswordPanel from './ResetPasswordPanel'
import SubmitPanel from './SubmitPanel'

const propTypes = {
  /**
   * If true, modal is shown.
   */
  show: PropTypes.bool,
  /**
   * Callback to hide the modal
   */
  onHide: PropTypes.func.isRequired,
  /**
   * If true, puts modal behind the backdrop, in case of another modal is opened on top of it for instance
   */
  toBackdrop: PropTypes.bool,
  /**
   * Employee to be offboarded
   */
  employee: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to update the employee list
   */
  onUpdate: PropTypes.func.isRequired,
}

function ResetPasswordModal(props) {
  const { show = false, onHide, toBackdrop = false, employee, onUpdate } = props ?? {}

  const intl = useIntl()

  const organizationId = useSelector(({ user }) => user?.data?.organization)
  const customerId = useSelector(getSelectedCustomerId)
  const userId = useSelector(({ user }) => user?.data?.id)
  const currentUserEmail = useSelector(({ user }) => user?.data?.amazon_id)
  const employeeFullName = getEmployeeFullName(employee)

  const allEmployees = useEngageEmployees({
    selectedCustomerId: customerId,
  })

  const employeeEmailOptions = useMemo(
    () =>
      allEmployees
        .filter((item) => !item.ignored)
        .map((item) => ({ label: `${getEmployeeFullName(item)} (${item.email})`, value: item.email }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)),
    [allEmployees],
  )

  // generating password here once the modal is opened to avoid confusion it could cause if we generated a new one every time the user toggled between manual and auto generation
  const autoGeneratedPassword = useMemo(() => generatePassword(), [])

  const initialValues = useMemo(
    () => buildInitialValues({ currentUserEmail, autoGeneratedPassword }),
    [currentUserEmail, autoGeneratedPassword],
  )

  const validationSchema = useMemo(() => getValidationSchema(intl), [intl])

  const { resetPassword, errorResetingPassword, successResetingPassword } = useEngageResetPassword()

  const onSubmit = useCallback(
    async (values) => {
      const payload = toApiPayload(values)

      await resetPassword({
        actions: {
          ...payload,
          employeeEmail: employee.email,
          employeeIdentifier: employee.employeeIdentifier,
        },
        customerId,
        userId,
        organizationId,
      })

      onUpdate()
    },
    [resetPassword, customerId, userId, organizationId, employee, onUpdate],
  )

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize // enableReinitialize flag forces the form to reset every time initial values change, thus initialValues has to be memoized correctly
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ dirty, isSubmitting }) => (
        <ModalSidebar
          show={show}
          onHide={onHide}
          size="md"
          shouldShowCloseConfirmation={() => dirty && !successResetingPassword}
          steps={MODAL_STEPS}
          initialStepKey={STEP_KEYS.RESET_PASSWORD}
          title="ENGAGE.RESET_PASSWORD"
          subTitles={[employeeFullName, intl.formatMessage({ id: 'ENGAGE.MICROSOFT_365' })]}
          variant="engage"
          isLoading={isSubmitting}
          loadingDescription="ENGAGE.RESETTING_PASSWORD"
          toBackdrop={toBackdrop}
        >
          <ModalPanelSwitch stepKey={STEP_KEYS.RESET_PASSWORD}>
            <ResetPasswordPanel
              autoGeneratedPassword={autoGeneratedPassword}
              employeeEmailOptions={employeeEmailOptions}
            />
          </ModalPanelSwitch>

          <ModalPanelSwitch stepKey={STEP_KEYS.SUBMIT}>
            <SubmitPanel errorResetingPassword={errorResetingPassword} />
          </ModalPanelSwitch>
        </ModalSidebar>
      )}
    </Formik>
  )
}

ResetPasswordModal.propTypes = propTypes

export default ResetPasswordModal
